import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

// Hook pour la liste des signalements
export const useSignalements = () => {
  const { apiCall } = useApi();
  const [signalements, setSignalements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    non_traites: 0,
    en_cours: 0,
    resolus: 0,
    fermes: 0
  });
  const [frequentUsers, setFrequentUsers] = useState([]);
  const [frequentPrestataires, setFrequentPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSignalements = useCallback(async (filters = {}) => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les signalements');
      setSignalements([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 'Tous') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = `/signalements${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setSignalements(data.signalements || []);
      setStats(data.stats || {});
      setFrequentUsers(data.frequent_users || []);
      setFrequentPrestataires(data.frequent_prestataires || []);
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const updateSignalementStatus = useCallback(async (id, status) => {
    try {
      const result = await apiCall(`/signalements/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      
      // Recharger les signalements après la mise à jour
      fetchSignalements();
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }, [fetchSignalements, apiCall]);

  useEffect(() => {
    fetchSignalements();
  }, [fetchSignalements]);

  return {
    signalements,
    stats,
    frequentUsers,
    frequentPrestataires,
    loading,
    error,
    refetch: fetchSignalements,
    updateStatus: updateSignalementStatus
  };
};

// Hook pour un signalement spécifique
export const useSignalement = (id) => {
  const { apiCall } = useApi();
  const [signalement, setSignalement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSignalement = useCallback(async () => {
    if (!id) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le signalement');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiCall(`/signalements/${id}`);
      setSignalement(data);
    } catch (error) {
      console.error('Erreur lors du chargement du signalement:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, apiCall]);

  const updateStatus = useCallback(async (status) => {
    try {
      const result = await apiCall(`/signalements/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      
      // Recharger le signalement après la mise à jour
      fetchSignalement();
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }, [id, fetchSignalement, apiCall]);

  const assignAdmin = useCallback(async (adminId) => {
    try {
      const result = await apiCall(`/signalements/${id}/assign`, {
        method: 'PUT',
        body: JSON.stringify({ admin_id: adminId })
      });
      
      fetchSignalement();
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      throw error;
    }
  }, [id, fetchSignalement, apiCall]);

  const addSanction = useCallback(async (sanctions) => {
    try {
      const result = await apiCall(`/signalements/${id}/sanctions`, {
        method: 'PUT',
        body: JSON.stringify({ sanctions })
      });
      
      fetchSignalement();
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'ajout des sanctions:', error);
      throw error;
    }
  }, [id, fetchSignalement, apiCall]);

  useEffect(() => {
    fetchSignalement();
  }, [fetchSignalement]);

  return {
    signalement,
    loading,
    error,
    refetch: fetchSignalement,
    updateStatus,
    assignAdmin,
    addSanction
  };
};

// Hook pour les messages d'un signalement
export const useSignalementMessages = (signalementId) => {
  const { apiCall } = useApi();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!signalementId) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les messages');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiCall(`/signalements/${signalementId}/messages`);
      setMessages(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [signalementId, apiCall]);

  const sendMessage = useCallback(async (content) => {
    try {
      const result = await apiCall(`/signalements/${signalementId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content })
      });
      
      // Recharger les messages après l'envoi
      fetchMessages();
      
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      throw error;
    }
  }, [signalementId, fetchMessages, apiCall]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    loading,
    error,
    sendMessage,
    refetch: fetchMessages
  };
};

// Hook pour les notes internes d'un signalement
export const useSignalementNotes = (signalementId) => {
  const { apiCall } = useApi();
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const saveNote = useCallback(async (noteContent) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiCall(`/signalements/${signalementId}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ notes: noteContent })
      });
      
      setNotes(noteContent);
      return result;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la note:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [signalementId, apiCall]);

  const addNote = useCallback(async (noteContent) => {
    return saveNote(noteContent);
  }, [saveNote]);

  return {
    notes,
    loading,
    error,
    saveNote,
    addNote
  };
};